/**
 * The data every template variable and every rule condition is resolved against.
 *
 * ## Why one context object
 *
 * A template can mention the orderer, the totals, the lines, their categories, the carrier and the
 * warehouse in a single sentence, and a rule can condition on all of those at once. Resolving each
 * variable against the database on its own would mean a template with twelve variables costing
 * twelve round trips, and a rule set of thirty rules re-reading the same order thirty times.
 *
 * So the server loads **one** context per order and every resolver is a pure function over it.
 * That is also what makes the preview honest: the same object is returned to the browser, so what
 * the author sees is rendered by the same code that will send.
 *
 * ## Snapshot versus live
 *
 * The fields under `order`, `customer` and `shipping` come from the order row, which is a total
 * snapshot (ADR-020) — the product name and the carrier name read the way they read when the order
 * was placed, forever. The classification on each item (`categoryPathIds`, `tagIds`, `brandName`)
 * has nowhere to live on the order and is therefore read **live**. The practical consequence, which
 * belongs in the reader's head and not only in a doc: a rule evaluated against a two-month-old
 * order uses today's categories.
 *
 * Money arrives here as `number`, already run through `toMoney`. Sequelize hands DECIMAL back as a
 * string, and a resolver that received the raw value would print "428.00" where a sibling prints
 * "428" — see the note on `Order`'s money columns.
 */

/** Israel, because a date in a message to a customer is a date in their day. */
export const IL_TIME_ZONE = 'Asia/Jerusalem';

/** One ordered line, with the live classification a condition needs. */
export interface OrderMailingItem {
  productName: string;
  variantLabel: string | null;
  sku: string | null;
  quantity: number;
  unitPrice: number;
  lineTotal: number;
  /**
   * Whether the line was matched to a catalogue variant at intake.
   *
   * `false` is a normal outcome, not a bug: intake accepts an order whose SKU we do not know
   * (`docs/store-orders-intake.md`) and records an exception for a human. Such a line has **no**
   * category, tag or brand, so a rule conditioned on any of them cannot match it. That is why the
   * flag is in the context rather than inferred from an empty array — the engine can then say
   * "the lines were never matched to the catalogue" instead of "no rule applied".
   */
  matched: boolean;
  productId: number | null;
  categoryId: number | null;
  categoryName: string | null;
  /** Root → leaf, inclusive, so one comparison answers "in this category or under it". */
  categoryPathIds: number[];
  brandId: number | null;
  brandName: string | null;
  tagIds: number[];
  tagNames: string[];
}

export interface OrderMailingContext {
  order: {
    id: number;
    /** What the customer quotes — not our primary key. */
    externalNumber: string;
    /** ISO. Formatted at render time in Israel time. */
    placedAt: string;
    currency: string;
    status: string;
    paymentStatus: string;
    fulfillmentStatus: string;
    itemsSubtotal: number;
    discountTotal: number;
    shippingTotal: number;
    taxTotal: number;
    grandTotal: number;
    unitsCount: number;
    linesCount: number;
    customerNote: string | null;
  };
  /**
   * The orderer as **the order** recorded them.
   *
   * Every field here is a column on `Orders`. Nothing in mailing reads the customer card: the
   * feature works against orders and orders alone, so a message is addressed to what the buyer
   * supplied at that checkout and a rule can be evaluated without a card existing at all.
   *
   * `customerId` is the order's own column, kept so a send can be attributed to a card in the
   * mailing log — a link *out* of mailing, never a value mailing depends on.
   */
  customer: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    customerId: number | null;
  };
  items: OrderMailingItem[];
  shipping: {
    methodId: number | null;
    methodName: string | null;
    methodType: string | null;
    providerId: number | null;
    providerName: string | null;
    trackingNumber: string | null;
    recipientName: string | null;
    street: string | null;
    houseNumber: string | null;
    apartment: string | null;
    city: string | null;
    zip: string | null;
    country: string | null;
    shippedAt: string | null;
    deliveredAt: string | null;
  };
  pickup: {
    pointId: number | null;
    pointName: string | null;
    readyForPickupAt: string | null;
  };
  warehouse: {
    id: number;
    name: string;
    address: string | null;
    phone: string | null;
  } | null;
  store: {
    id: number;
    name: string;
  };
}

/* ------------------------------------------------------------------ */
/* Formatting — one implementation, because a message shows both       */
/* ------------------------------------------------------------------ */

/**
 * `1234.5` → `1,234.50 ₪`, `428` → `428 ₪`.
 *
 * Trailing `.00` is dropped because a whole-shekel total reads as a price and `428.00 ₪` reads as
 * an accounting export. Anything with agorot keeps both digits — `1,234.5 ₪` looks like a typo.
 */
export const formatTemplateMoney = (value: number, currency = 'ILS'): string => {
  const rounded = Math.round(value * 100) / 100;
  const digits = Number.isInteger(rounded) ? 0 : 2;
  const amount = rounded.toLocaleString('he-IL', { minimumFractionDigits: digits, maximumFractionDigits: digits });
  return currency === 'ILS' ? `${amount} ₪` : `${amount} ${currency}`;
};

// `Object.fromEntries` is ES2019 and this library compiles against `lib: es2017` — see the note in
// PROGRESS.md about `flatMap` not existing here either. The reduce is not a style choice, and the
// named result type is what keeps `noPropertyAccessFromIndexSignature` happy.
interface IlDateParts {
  day: string;
  month: string;
  year: string;
  hour: string;
  minute: string;
}

const ilParts = (iso: string): IlDateParts | null => {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return null;
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: IL_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  }).formatToParts(at);

  const pick = (type: string): string => parts.filter(part => part.type === type).map(part => part.value)[0] ?? '';
  return { day: pick('day'), month: pick('month'), year: pick('year'), hour: pick('hour'), minute: pick('minute') };
};

/** `dd/MM/yyyy`, empty string for a null or unparseable value — never the word "Invalid Date". */
export const formatTemplateDate = (iso: string | null): string => {
  if (!iso) return '';
  const p = ilParts(iso);
  return p ? `${p.day}/${p.month}/${p.year}` : '';
};

/** `dd/MM/yyyy HH:mm`. Used where the hour is part of the fact (when an order was placed). */
export const formatTemplateDateTime = (iso: string | null): string => {
  if (!iso) return '';
  const p = ilParts(iso);
  return p ? `${p.day}/${p.month}/${p.year} ${p.hour}:${p.minute}` : '';
};

/** The delivery address on one line, skipping the parts the shop did not send. */
export const formatTemplateAddress = (shipping: OrderMailingContext['shipping']): string => {
  const street = [shipping.street, shipping.houseNumber].filter(Boolean).join(' ');
  const withApartment = shipping.apartment ? `${street} דירה ${shipping.apartment}` : street;
  return [withApartment, shipping.city, shipping.zip].map(part => (part ?? '').trim()).filter(Boolean).join(', ');
};

/** Distinct, order-preserving, blanks dropped — every list variable ends up here. */
export const uniqueStrings = (values: (string | null | undefined)[]): string[] => {
  const seen = new Set<string>();
  const out: string[] = [];
  values.forEach(value => {
    const trimmed = (value ?? '').trim();
    if (!trimmed || seen.has(trimmed)) return;
    seen.add(trimmed);
    out.push(trimmed);
  });
  return out;
};

export const uniqueNumbers = (values: number[]): number[] => {
  const out: number[] = [];
  values.forEach(value => {
    if (out.indexOf(value) === -1) out.push(value);
  });
  return out;
};
